import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Mic, MicOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAudioRecorder } from "react-audio-voice-recorder";
import ContextInputPanel, { type ContextResult } from "./ContextInputPanel";
import CaptureHeader from "./CaptureHeader";
import LockedFeatureSheet from "@/components/LockedFeatureSheet";
import { useEntitlements } from "@/hooks/useEntitlements";

interface VoiceCaptureProps {
  onComplete: (result: { audioBlob?: Blob; audioDurationSeconds?: number; writtenText?: string; contextMode?: 'voice' | 'written'; contextBlob?: Blob; contextText?: string; approvalStatus?: 'pending' | 'private' }) => void;
  onCancel: () => void;
  /** Optional back handler — when provided, a back arrow is rendered. The
   *  orchestrator wires this to `goBack()` so users can return to the type
   *  picker without losing collection/card context. */
  onBack?: () => void;
  /** When provided (voice_moment only), shows "Save for later" button after recording. */
  onFinishLater?: (result: { audioBlob: Blob; audioDurationSeconds: number }) => void;
  onWriteInstead?: () => void;
  contextPrompt?: string;
  cardContext?: { title: string; body: string } | null;
  voiceRequired?: boolean;
  /** Hex accent from the capture's collection context. Falls back to brand. */
  accentColor?: string;
  /** When true, auto-start recording on mount (PWA shortcut entry). */
  autoStart?: boolean;
  captureType?: string;
}

function formatTime(s: number) {
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export default function VoiceCapture({
  onComplete,
  onCancel,
  onBack,
  onFinishLater,
  onWriteInstead,
  contextPrompt,
  cardContext,
  voiceRequired = false,
  accentColor,
  autoStart = false,
  captureType,
}: VoiceCaptureProps) {
  const { t } = useTranslation();
  const {
    maxVoiceDurationSeconds,
    voiceRemainingThisWeek,
    canVoiceContext,
    isLoading: entitlementsLoading,
  } = useEntitlements();
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [started, setStarted] = useState(false);
  const shouldCaptureRef = useRef(false);
  const stoppedDurationRef = useRef(0);
  // Weekly cap applies ONLY to voice_moment — the Seedling user's one
  // dedicated voice memory per week. Duration cap applies to any fresh
  // voice capture (voice_moment or general_memory voice mode).
  const capturesVoiceMemory = captureType === "voice_moment";
  const capturesAudio =
    captureType === "voice_moment" || captureType === "general_memory";
  const weeklyExhausted =
    !entitlementsLoading &&
    capturesVoiceMemory &&
    voiceRemainingThisWeek === 0;
  // In general_memory, voice mode is a premium feature gated by
  // can_voice_context. Free tiers (Seedling/Ember) are forced to Write.
  // Treat still-loading entitlements as locked — otherwise a cold-start
  // free user (no cache yet) would evaluate as unlocked and land on Speak
  // before the real flag arrived.
  const voiceContextLocked =
    captureType === "general_memory" && (entitlementsLoading || !canVoiceContext);
  const [mode, setMode] = useState<'voice' | 'written'>(
    voiceContextLocked ? "written" : "voice",
  );
  const [writtenText, setWrittenText] = useState('');
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
  const [capturedDuration, setCapturedDuration] = useState(0);
  const [previewAudioUrl, setPreviewAudioUrl] = useState<string | null>(null);
  const [capPaywallOpen, setCapPaywallOpen] = useState(false);
  const [contextLockOpen, setContextLockOpen] = useState(false);
  // Fires once per recording session when the duration cap auto-stops us.
  const capStoppedRef = useRef(false);

  useEffect(() => {
    if (!capturedBlob) { setPreviewAudioUrl(null); return; }
    const url = URL.createObjectURL(capturedBlob);
    setPreviewAudioUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [capturedBlob]);

  // Keep `mode` in sync with the entitlement lock once it resolves. Without
  // this, a free user who rendered during the entitlements fetch stays on
  // the voice tab because `useState` never re-runs its initializer.
  useEffect(() => {
    if (entitlementsLoading) return;
    if (captureType !== "general_memory") return;
    if (voiceContextLocked && mode === "voice") {
      setMode("written");
    } else if (!voiceContextLocked && mode === "written" && !writtenText.trim() && !capturedBlob) {
      setMode("voice");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceContextLocked, entitlementsLoading]);

  const {
    startRecording,
    stopRecording,
    recordingBlob,
    recordingTime,
    isRecording,
  } = useAudioRecorder(
    undefined,
    (err: DOMException) => {
      if (err.name === "NotAllowedError" || err.name === "NotFoundError") {
        setPermissionDenied(true);
      }
    },
  );

  // Capture blob when recording stops
  useEffect(() => {
    if (!recordingBlob || !shouldCaptureRef.current) return;
    shouldCaptureRef.current = false;
    if (onFinishLater) {
      // Show the two-button choice inline
      setCapturedBlob(recordingBlob);
      setCapturedDuration(stoppedDurationRef.current);
    } else {
      // Immediate submit (general_memory, time_capsule, context step)
      const result: Parameters<VoiceCaptureProps['onComplete']>[0] = {
        audioBlob: recordingBlob,
        audioDurationSeconds: stoppedDurationRef.current,
      };
      if (captureType === 'general_memory') result.contextMode = 'voice';
      onComplete(result);
    }
  }, [recordingBlob]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStart = () => {
    // Block the start if Seedling has used their weekly voice memory. The
    // paywall is the only affordance — we don't auto-switch to written mode
    // on voice_moment because that capture type has no written path.
    if (capturesVoiceMemory && weeklyExhausted) {
      setCapPaywallOpen(true);
      return;
    }
    // Defensive — voice mode on general_memory should be unreachable for
    // locked tiers (mode defaults to 'written'), but guard anyway.
    if (voiceContextLocked) {
      setContextLockOpen(true);
      return;
    }
    capStoppedRef.current = false;
    setStarted(true);
    startRecording();
  };

  // PWA shortcut entry — begin recording immediately on mount. Wait for
  // entitlements to resolve so the tier gate inside handleStart has the
  // real flags to evaluate against.
  useEffect(() => {
    if (autoStart && !started && !permissionDenied && !entitlementsLoading) {
      handleStart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, entitlementsLoading]);

  // Duration cap auto-stop. Watched while recording; when the tier ceiling
  // is reached we stop and commit the recording exactly like a manual Done.
  useEffect(() => {
    if (!isRecording || !capturesAudio) return;
    if (maxVoiceDurationSeconds === null) return; // unlimited
    if (capStoppedRef.current) return;
    if (recordingTime >= maxVoiceDurationSeconds) {
      capStoppedRef.current = true;
      shouldCaptureRef.current = true;
      stoppedDurationRef.current = recordingTime;
      stopRecording();
    }
  }, [recordingTime, isRecording, capturesAudio, maxVoiceDurationSeconds, stopRecording]);

  const handleDone = () => {
    shouldCaptureRef.current = true;
    stoppedDurationRef.current = recordingTime;
    stopRecording();
  };

  const handleCancel = () => {
    if (isRecording) stopRecording();
    onCancel();
  };

  const handleModeSwitch = (next: 'voice' | 'written') => {
    if (next === mode) return;
    // Voice tab is locked entirely for free tiers on general_memory —
    // open the voice_context paywall and stay on Write.
    if (next === 'voice' && voiceContextLocked) {
      setContextLockOpen(true);
      return;
    }
    // Voice tab is dead-end on cap exhaustion. Open the paywall but keep the
    // user on the written tab they were on — avoids a mode flip with no UI.
    if (next === 'voice' && capturesVoiceMemory && weeklyExhausted) {
      setCapPaywallOpen(true);
      return;
    }
    if (isRecording) stopRecording();
    setStarted(false);
    setMode(next);
  };

  const handleWrittenSave = () => {
    if (!writtenText.trim()) return;
    onComplete({ writtenText: writtenText.trim(), contextMode: 'written' });
  };

  if (permissionDenied) {
    return (
      <div className="flex flex-col items-center gap-4 py-8">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
          <MicOff className="h-7 w-7 text-muted-foreground" />
        </div>
        <div className="space-y-1 text-center">
          <p className="text-base font-medium text-foreground">
            {t("recording.voice.permission.title")}
          </p>
          <p className="text-sm text-muted-foreground">
            {t("recording.voice.permission.body")}
          </p>
        </div>
        <Button variant="outline" onClick={onCancel} className="px-8">
          {t("common.close")}
        </Button>
      </div>
    );
  }

  // Header state — single title per captureType, shown across idle / recording
  // / preview. The recording state is already signalled by the pulsing mic +
  // timer + "Mori is listening…", so the title stays type-anchored.
  // Back: type-picker when idle, none while recording (cancel handles exit),
  // retake in preview. The Overlay's X still handles full dismiss.
  const titleKey =
    captureType === "general_memory" ? "capture.title.general_memory"
    : captureType === "time_capsule" ? "capture.title.time_capsule"
    : "capture.title.voice_moment";
  const headerTitle = t(titleKey);
  const headerOnBack = capturedBlob
    ? () => { setCapturedBlob(null); setCapturedDuration(0); setStarted(false); }
    : !started && onBack
      ? () => { if (isRecording) stopRecording(); onBack(); }
      : undefined;
  const headerBackLabel = capturedBlob ? t("capture.preview.retake") : t("common.back");

  return (
    <div className="flex flex-col items-center gap-6 w-full max-w-sm mx-auto px-6 pt-8">
      <CaptureHeader title={headerTitle} onBack={headerOnBack} backLabel={headerBackLabel} />
      {/* Segmented toggle — only for general_memory. Voice tab is locked
          on free tiers; tapping opens the voice_context paywall. */}
      {captureType === 'general_memory' && (
        <div className="flex items-center gap-1 rounded-full bg-muted p-1 w-full max-w-[220px]">
          <button
            type="button"
            onClick={() => handleModeSwitch('voice')}
            className={`flex-1 px-4 py-1.5 text-sm font-medium rounded-full transition-colors ${mode === 'voice' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'} ${voiceContextLocked ? 'opacity-60' : ''}`}
          >
            {voiceContextLocked ? '🔒 ' : '🎤 '}{t("button.speak")}
          </button>
          <button
            type="button"
            onClick={() => handleModeSwitch('written')}
            className={`flex-1 px-4 py-1.5 text-sm font-medium rounded-full transition-colors ${mode === 'written' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}
          >
            ✍️ {t("button.write")}
          </button>
        </div>
      )}

      {/* Written mode */}
      {mode === 'written' && (
        <>
          {cardContext ? (
            <div
              className="w-full rounded-xl flex items-center gap-2"
              style={{ backgroundColor: "#EEEDFE", border: "0.5px solid #AFA9EC", padding: "8px 12px" }}
            >
              <p className="flex-1 truncate" style={{ fontSize: 13, fontWeight: 500, color: "#3C3489" }}>
                {cardContext.title}
              </p>
            </div>
          ) : contextPrompt ? (
            <div
              className="w-full rounded-xl px-4 py-3 text-sm font-medium text-center"
              style={{ backgroundColor: "rgba(196, 91, 75, 0.10)", color: "#8B3A2E" }}
            >
              {contextPrompt}
            </div>
          ) : null}
          <textarea
            value={writtenText}
            onChange={(e) => setWrittenText(e.target.value)}
            placeholder={t("placeholder.voicePrompt")}
            autoFocus
            className="w-full min-h-[120px] rounded-xl border border-border bg-background p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <div className="flex gap-3 w-full">
            <Button variant="ghost" onClick={handleCancel} className="px-6">{t("common.cancel")}</Button>
            <Button
              onClick={handleWrittenSave}
              disabled={!writtenText.trim()}
              className="flex-1"
              style={accentColor ? { backgroundColor: accentColor, color: "#FFFFFF" } : undefined}
            >
              {t("common.save")}
            </Button>
          </div>
        </>
      )}

      {/* After recording: playback + context panel + actions */}
      {capturedBlob && (
        <div className="flex flex-col items-center gap-4 w-full">
          {previewAudioUrl && (
            <audio
              src={previewAudioUrl}
              controls
              className="w-full h-10"
              preload="metadata"
            />
          )}
          <ContextInputPanel
            onProcess={(ctx: ContextResult) => onComplete({ audioBlob: capturedBlob, audioDurationSeconds: capturedDuration, ...ctx })}
            onSaveLater={onFinishLater ? () => onFinishLater({ audioBlob: capturedBlob!, audioDurationSeconds: capturedDuration }) : undefined}
            accentColor={accentColor}
          />
        </div>
      )}

      {/* Voice mode */}
      {mode === 'voice' && !capturedBlob && <>
      {/* Seedling weekly-cap paywall — dead-end state for voice_moment, and
          in general_memory it just prompts them back to the written tab. */}
      {capturesVoiceMemory && weeklyExhausted ? (
        <div className="w-full flex flex-col items-center gap-4 py-8">
          <span style={{ fontSize: 32, lineHeight: 1 }}>📼</span>
          <div className="text-center space-y-1">
            <p className="text-base font-medium text-foreground">
              {t("capture.limits.voiceWeeklyExhaustedTitle")}
            </p>
            <p className="text-sm text-muted-foreground max-w-[260px]">
              {t("capture.limits.voiceWeeklyExhaustedBody")}
            </p>
          </div>
          <Button onClick={() => setCapPaywallOpen(true)} className="px-6">
            {t("capture.limits.upgrade")}
          </Button>
        </div>
      ) : (<>
      {/* Card context compact banner */}
      {cardContext ? (
        <div
          className="w-full rounded-xl flex items-center gap-2"
          style={{ backgroundColor: "#EEEDFE", border: "0.5px solid #AFA9EC", padding: "8px 12px" }}
        >
          <p className="flex-1 truncate" style={{ fontSize: 13, fontWeight: 500, color: "#3C3489" }}>
            {cardContext.title}
          </p>
        </div>
      ) : contextPrompt ? (
        <div
          className="w-full rounded-xl px-4 py-3 text-sm font-medium text-center"
          style={{ backgroundColor: "rgba(196, 91, 75, 0.10)", color: "#8B3A2E" }}
        >
          {contextPrompt}
        </div>
      ) : null}

      {/* Duration hint — shown pre-record when the tier has a ceiling. */}
      {capturesAudio && !started && maxVoiceDurationSeconds !== null && (
        <p className="text-[11px] text-muted-foreground/80 text-center">
          {t("capture.limits.voiceMaxBanner", { seconds: maxVoiceDurationSeconds })}
        </p>
      )}

      {/* Mic button / animation */}
      <div className="relative flex items-center justify-center mt-4">
        {started && (
          <>
            <div
              className="absolute h-32 w-32 rounded-full animate-recording-pulse"
              style={accentColor ? { backgroundColor: `${accentColor}1A` } : undefined}
            />
            <div
              className="absolute h-24 w-24 rounded-full animate-recording-pulse"
              style={{
                animationDelay: "0.5s",
                ...(accentColor ? { backgroundColor: `${accentColor}26` } : {}),
              }}
            />
            {/* Brand fallback rings — only shown when no accent is active */}
            {!accentColor && (
              <>
                <div className="absolute h-32 w-32 rounded-full bg-primary/10 animate-recording-pulse" />
                <div
                  className="absolute h-24 w-24 rounded-full bg-primary/15 animate-recording-pulse"
                  style={{ animationDelay: "0.5s" }}
                />
              </>
            )}
          </>
        )}
        <button
          type="button"
          onClick={!started ? handleStart : undefined}
          className={`flex h-16 w-16 items-center justify-center rounded-full transition-transform${!started ? " active:scale-95" : ""} ${accentColor ? "" : "bg-primary"}`}
          style={accentColor ? { backgroundColor: accentColor } : undefined}
        >
          {captureType === "voice_moment" ? (
            <span className="text-3xl leading-none">📼</span>
          ) : (
            <Mic className="h-7 w-7 text-primary-foreground" />
          )}
        </button>
      </div>

      {/* Timer / tap prompt */}
      {started ? (
        <>
          <p className="text-3xl font-semibold tabular-nums text-foreground mt-4">
            {formatTime(recordingTime)}
            {capturesAudio && maxVoiceDurationSeconds !== null && (
              <span className="ml-1 text-sm font-normal text-muted-foreground/70">
                / {formatTime(maxVoiceDurationSeconds)}
              </span>
            )}
          </p>
          <p className="text-sm text-muted-foreground">
            {t("mori.listening")}
          </p>
          <p className="text-[13px] italic text-muted-foreground/80 text-center leading-snug px-2">
            🐿️ {t("capture.mori_hint")}
          </p>
        </>
      ) : (
        <p className="text-sm text-muted-foreground mt-4">
          {t("recording.voice.tapToStart")}
        </p>
      )}

      {/* Controls */}
      <div className="flex gap-3">
        <Button variant="ghost" onClick={handleCancel} className="px-6">{t("common.cancel")}</Button>
        {started && (
          <Button
            onClick={handleDone}
            className="px-8"
            style={accentColor ? { backgroundColor: accentColor, color: "#FFFFFF" } : undefined}
          >
            {t("common.done")}
          </Button>
        )}
      </div>

      {/* Write instead */}
      {!voiceRequired && onWriteInstead && (
        <button
          type="button"
          onClick={() => {
            if (isRecording) stopRecording();
            onWriteInstead();
          }}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          {t("recording.voice.writeInstead")}
        </button>
      )}
      {voiceRequired && (
        <p className="text-xs text-muted-foreground/70 text-center">
          {t("recording.voice.voiceRequired")}
        </p>
      )}
      </>)}
      </>}

      <LockedFeatureSheet
        feature="voice_cap"
        open={capPaywallOpen}
        onClose={() => setCapPaywallOpen(false)}
      />
      <LockedFeatureSheet
        feature="voice_context"
        open={contextLockOpen}
        onClose={() => setContextLockOpen(false)}
      />
    </div>
  );
}
